import { cva, type VariantProps } from 'class-variance-authority';
import { Search, X } from 'lucide-react';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '../lib/cn';
import { IconButton } from './button';

const controlBase = [
  'w-full min-w-0 rounded-md border bg-surface text-fg transition-colors',
  'placeholder:text-fg-faint',
  'focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-focus',
  'disabled:cursor-not-allowed disabled:bg-surface-hover disabled:text-fg-muted',
  'read-only:bg-surface-subtle',
];

const inputVariants = cva(controlBase, {
  variants: {
    size: {
      sm: 'h-control-sm px-2 text-xs',
      md: 'h-control px-2.5 text-[0.8125rem]',
      lg: 'h-control-lg px-3 text-sm',
    },
    invalid: {
      true: 'border-danger-border focus-visible:outline-danger',
      false: 'border-border hover:border-border-strong',
    },
  },
  defaultVariants: { size: 'md', invalid: false },
});

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Decoration rendered inside the field, before the text. */
  icon?: ReactNode;
  /** Decoration rendered inside the field, after the text. */
  affix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size, invalid, icon, affix, ...props },
  ref,
) {
  const field = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(inputVariants({ size, invalid }), icon && 'pl-8', affix && 'pr-8', className)}
      {...props}
    />
  );
  if (!icon && !affix) return field;
  return (
    <div className="relative flex w-full items-center">
      {icon && (
        <span className="pointer-events-none absolute left-2.5 flex text-fg-faint [&_svg]:size-4" aria-hidden>
          {icon}
        </span>
      )}
      {field}
      {affix && <span className="absolute right-1.5 flex items-center">{affix}</span>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 3, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        controlBase,
        'resize-y px-2.5 py-2 text-[0.8125rem] leading-relaxed',
        invalid ? 'border-danger-border focus-visible:outline-danger' : 'border-border hover:border-border-strong',
        className,
      )}
      {...props}
    />
  );
});

export interface NativeSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

/**
 * A styled native `<select>`.
 *
 * Deliberately native rather than a Radix listbox for the dozens of short,
 * schema-driven option lists in the chart properties panel: the platform control
 * is keyboard- and screen-reader-correct for free, and on touch it opens the
 * device picker. `Select` (Radix) is reserved for lists that need groups,
 * descriptions or search.
 */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { className, size, invalid, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        inputVariants({ size, invalid }),
        'cursor-pointer appearance-none bg-no-repeat pr-7',
        "bg-[image:var(--select-caret)] bg-[position:right_0.5rem_center] bg-[size:0.7rem]",
        className,
      )}
      style={{
        // Inline so the caret follows the text colour in both themes without a
        // second asset or a wrapper element.
        ['--select-caret' as string]:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23888' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5 6 6.5 11 1.5'/%3E%3C/svg%3E\")",
      }}
      {...props}
    >
      {children}
    </select>
  );
});

export interface SearchInputProps extends Omit<InputProps, 'icon' | 'affix' | 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Keyboard hint rendered on the right while the field is empty. */
  shortcut?: string;
}

/** A search field with a clear affordance and an optional shortcut hint. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, shortcut, className, placeholder = 'Search…', ...props },
  ref,
) {
  return (
    <Input
      ref={ref}
      type="search"
      role="searchbox"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && value) {
          e.preventDefault();
          onValueChange('');
        }
      }}
      placeholder={placeholder}
      icon={<Search />}
      className={cn('[&::-webkit-search-cancel-button]:hidden', className)}
      affix={
        value ? (
          <IconButton label="Clear search" size="sm" onClick={() => onValueChange('')}>
            <X />
          </IconButton>
        ) : shortcut ? (
          <kbd className="pointer-events-none mr-1 rounded border border-border bg-surface-hover px-1 py-px font-mono text-[0.625rem] text-fg-faint">
            {shortcut}
          </kbd>
        ) : undefined
      }
      {...props}
    />
  );
});

export interface FieldProps {
  label: string;
  /** Visually hide the label but keep it for assistive tech. */
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  /** Rendered to the right of the label — a unit, a reset, a live value. */
  aside?: ReactNode;
  children: (props: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}

/**
 * One labelled form control, wired for assistive tech: the label is a real
 * `<label for>`, the hint and the error are both referenced by
 * `aria-describedby`, and the error is announced when it appears.
 */
export function Field({ label, hideLabel, hint, error, required, className, aside, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className={cn('flex items-baseline justify-between gap-2', hideLabel && 'sr-only')}>
        <label htmlFor={id} className="text-xs font-medium text-fg-secondary">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          )}
        </label>
        {aside}
      </div>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint && !error && (
        <p id={hintId} className="text-xs leading-snug text-fg-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium leading-snug text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export { inputVariants };
